import {
	createLoadPersistentStateAction,
	createPersistableState,
	RevivedState,
	type StateToRevive,
	StoredStateKeys,
} from '~/Store/actions';
import { productSearchableDataSource } from '~/ProductSearchableDataSource/productSearchPersistentState';

import type { DispatchGetState } from '~/Store';
import type { PersistedState } from '~/Store/actions';
import type { PublicApi } from '~/Types/helpers';

const STATE_STORAGE_KEY = 'pm-webapp-state';

export type IPersistentStateManager = PublicApi<PersistentStateManager>;

export type Reviver<Key extends StoredStateKeys> = (
	stateObject: StateToRevive
) => { [ResultKey in keyof RevivedState[Key]]: RevivedState[Key][ResultKey] } | null;

export type Revivers = {
	[StoredStateKeys.productSearchableDataSource]: Reviver<StoredStateKeys.productSearchableDataSource>;
};

const defaultRevivers: Revivers = {
	[StoredStateKeys.productSearchableDataSource]: productSearchableDataSource,
};

export default class PersistentStateManager implements IPersistentStateManager {
	constructor(private readonly store: DispatchGetState, private readonly services: { window: typeof window }) {}

	public persistCurrentUser(): void {
		const state = this.store.getState();

		if (!state.accessToken.accessToken) {
			return;
		}

		this.persistInternal(createPersistableState(state), state.accessToken.accessToken.user.id);
	}

	private persistInternal(state: PersistedState, userId: string): void {
		let previousState = {};
		try {
			previousState = JSON.parse(this.services.window.localStorage.getItem(STATE_STORAGE_KEY) || '') || {};
		} catch (err) {}

		return this.services.window.localStorage.setItem(
			STATE_STORAGE_KEY,
			JSON.stringify({ ...previousState, [userId]: state })
		);
	}

	public loadStateSafe(userId: string): void {
		try {
			this.loadState(userId);
		} catch (error) {
			console.warn('Could not load the state:', error);
		}
	}

	public loadState(userId: string): void {
		const loadedState = this.loadStateInternal(userId, defaultRevivers);

		if (loadedState) {
			this.store.dispatch(createLoadPersistentStateAction(loadedState));
		}
	}

	private loadStateInternal(userId: string, revivers: Revivers): RevivedState {
		let stateObject: { [userId: string]: PersistedState | undefined } | null = null;
		try {
			stateObject = JSON.parse(this.services.window.localStorage.getItem(STATE_STORAGE_KEY) || '');
		} catch (e) {
			stateObject = null;
		}

		if (!stateObject) {
			throw new Error('State is not available in the local storage!');
		}

		const userState = stateObject[userId];

		if (!userState) {
			throw new Error('User state was not in the local storage');
		}

		const reviverKeys = Object.entries(revivers);
		return reviverKeys.reduce(
			(result: RevivedState, [key, reviver]) => ({
				...result,
				[key]: reviver(userState),
			}),
			{} as RevivedState
		);
	}

	public flush(userId: string): void {
		try {
			const parsed = JSON.parse(this.services.window.localStorage.getItem(STATE_STORAGE_KEY) || '');
			delete parsed[userId];

			this.services.window.localStorage.setItem(STATE_STORAGE_KEY, parsed);
		} catch (err) {
			console.warn(`Could not flush user persisted state, user: ${userId}`, err);
		}
	}
}
